import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Modal, Form, 
  Alert, Spinner, Badge, InputGroup
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaShieldAlt, FaSearch, FaUsers
} from 'react-icons/fa';
import AdminLayout from '../../components/layout/AdminLayout';
import supabase from '../../utils/supabaseClient';
import { useAuth } from '../../hooks/useAuth';

const PermissionManagement = () => {
  const { isAdmin } = useAuth();
  
  // Estados principais
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // 'create', 'edit'
  const [selectedPermission, setSelectedPermission] = useState(null);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Estado para filtros
  const [searchTerm, setSearchTerm] = useState('');

  // Carregar dados
  useEffect(() => {
    if (isAdmin) {
      loadPermissions();
    }
  }, [isAdmin]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      
      // Buscar permissões com contagem de onde são usadas (system_roles)
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('permissions')
        .select('*')
        .order('name');

      if (permissionsError) throw permissionsError;

      // Para cada permissão, contar quantos system_roles a usam
      const permissionsWithUsage = await Promise.all(
        permissionsData.map(async (permission) => {
          // Contar system_roles que usam esta permissão
          const { data: systemRoles, error: countError } = await supabase
            .from('system_roles')
            .select(`
              id,
              name,
              permissions,
              systems:system_id (name)
            `)
            .not('permissions', 'is', null);

          if (countError) {
            console.warn('Erro ao contar usage da permissão:', countError);
            return {
              ...permission,
              usageCount: 0,
              usedIn: []
            };
          }

          // Filtrar roles que realmente usam esta permissão
          const rolesUsingPermission = systemRoles.filter(role => 
            role.permissions && role.permissions[permission.name] === true
          );

          return {
            ...permission,
            usageCount: rolesUsingPermission.length,
            usedIn: rolesUsingPermission.map(role => ({
              roleName: role.name,
              systemName: role.systems?.name || 'Sistema desconhecido'
            }))
          };
        })
      );

      setPermissions(permissionsWithUsage);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setError('Erro ao carregar permissões: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePermission = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validações
      if (!formData.name.trim()) {
        throw new Error('O nome da permissão é obrigatório');
      }

      // Validar formato snake_case
      const snakeCasePattern = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;
      if (!snakeCasePattern.test(formData.name.trim())) {
        throw new Error('O nome deve estar no formato snake_case (ex: manage_users, create_products)');
      }

      // Verificar se já existe uma permissão com esse nome
      const { data: existingPermission } = await supabase
        .from('permissions')
        .select('id')
        .eq('name', formData.name.trim())
        .single();

      if (existingPermission) {
        throw new Error('Já existe uma permissão com este nome');
      }

      // Criar permissão
      const { data: newPermission, error: permissionError } = await supabase
        .from('permissions')
        .insert({
          name: formData.name.trim(),
          description: formData.description.trim()
        })
        .select()
        .single();

      if (permissionError) throw permissionError;

      setSuccess('Permissão criada com sucesso!');
      setShowModal(false);
      resetForm();
      loadPermissions();

    } catch (error) {
      console.error('Erro ao criar permissão:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermission = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validações
      if (!formData.name.trim()) {
        throw new Error('O nome da permissão é obrigatório');
      }

      // Validar formato snake_case
      const snakeCasePattern = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;
      if (!snakeCasePattern.test(formData.name.trim())) {
        throw new Error('O nome deve estar no formato snake_case (ex: manage_users, create_products)');
      }

      // Verificar se já existe outra permissão com esse nome
      const { data: existingPermission } = await supabase
        .from('permissions')
        .select('id')
        .eq('name', formData.name.trim())
        .neq('id', selectedPermission.id)
        .single();

      if (existingPermission) {
        throw new Error('Já existe uma permissão com este nome');
      }

      // Atualizar permissão
      const { error: permissionError } = await supabase
        .from('permissions')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim()
        })
        .eq('id', selectedPermission.id);

      if (permissionError) throw permissionError;

      setSuccess('Permissão atualizada com sucesso!');
      setShowModal(false);
      resetForm();
      loadPermissions();

    } catch (error) {
      console.error('Erro ao atualizar permissão:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePermission = async (permissionId) => {
    // Verificar se a permissão está sendo usada
    const permission = permissions.find(p => p.id === permissionId);
    if (permission.usageCount > 0) {
      setError(`Não é possível excluir a permissão "${permission.name}" pois ela está sendo usada em ${permission.usageCount} perfil(s). Remova dos perfis primeiro.`);
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir a permissão "${permission.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Remover permissão
      const { error: permissionError } = await supabase
        .from('permissions')
        .delete()
        .eq('id', permissionId);

      if (permissionError) throw permissionError;

      setSuccess(`Permissão "${permission.name}" excluída com sucesso!`);
      loadPermissions();

    } catch (error) {
      console.error('Erro ao excluir permissão:', error);
      setError('Erro ao excluir permissão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, permission = null) => {
    setModalType(type);
    setSelectedPermission(permission);
    setShowModal(true);

    if (type === 'edit' && permission) {
      setFormData({
        name: permission.name,
        description: permission.description || ''
      });
    } else {
      resetForm();
    }
    setError(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPermission(null);
    resetForm();
    setError(null);
  };

  // Filtrar permissões
  const filteredPermissions = permissions.filter(permission => 
    permission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatarData = (dataString) => {
    return new Date(dataString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Sugestões de permissões comuns para sistemas de hub/administração
  const permissoesSugeridas = [
    { name: 'manage_users', description: 'Gerenciar usuários do sistema' },
    { name: 'create_users', description: 'Criar novos usuários' },
    { name: 'edit_users', description: 'Editar dados de usuários' },
    { name: 'delete_users', description: 'Excluir usuários' },
    { name: 'manage_systems', description: 'Gerenciar sistemas do hub' },
    { name: 'manage_profiles', description: 'Gerenciar perfis de acesso' },
    { name: 'manage_permissions', description: 'Gerenciar permissões do sistema' },
    { name: 'manage_user_access', description: 'Conceder/revogar acessos de usuários' },
    { name: 'view_dashboard', description: 'Visualizar dashboard principal' },
    { name: 'view_reports', description: 'Visualizar relatórios' },
    { name: 'export_data', description: 'Exportar dados do sistema' },
    { name: 'manage_products', description: 'Gerenciar produtos' },
    { name: 'manage_sales', description: 'Gerenciar vendas' },
    { name: 'manage_customers', description: 'Gerenciar clientes' },
    { name: 'manage_financial', description: 'Acessar módulo financeiro' },
    { name: 'manage_inventory', description: 'Gerenciar estoque' },
    { name: 'approve_sales', description: 'Aprovar vendas' },
    { name: 'view_analytics', description: 'Visualizar análises e métricas' }
  ];

  const adicionarPermissaoSugerida = (permissao) => {
    setFormData({
      name: permissao.name,
      description: permissao.description
    });
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center mt-5">
          <h3>Acesso Negado</h3>
          <p>Você não tem permissão para gerenciar permissões.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container fluid>
        {/* Cabeçalho */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-1">
                  <FaShieldAlt className="me-2" />
                  Gerenciamento de Permissões
                </h1>
                <p className="text-muted mb-0">
                  Configure as permissões disponíveis para usar nos perfis dos sistemas
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  onClick={loadPermissions}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => openModal('create')}
                >
                  <FaPlus className="me-2" />
                  Nova Permissão
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Mensagens */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={8}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nome ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setSearchTerm('')}
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Estatísticas */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="border-0 bg-primary text-white">
              <Card.Body className="text-center">
                <h4>{permissions.length}</h4>
                <small>Total de Permissões</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 bg-success text-white">
              <Card.Body className="text-center">
                <h4>{permissions.filter(p => p.usageCount > 0).length}</h4>
                <small>Permissões em Uso</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 bg-warning text-white">
              <Card.Body className="text-center">
                <h4>{permissions.filter(p => p.usageCount === 0).length}</h4>
                <small>Permissões Não Utilizadas</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabela de permissões */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Permissões Cadastradas ({filteredPermissions.length})
            </h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Carregando permissões...</p>
              </div>
            ) : (
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Descrição</th>
                    <th>Usado em</th>
                    <th>Data de Criação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.map(permission => (
                    <tr key={permission.id}>
                      <td>
                        <code className="bg-light p-1 rounded">{permission.name}</code>
                      </td>
                      <td>{permission.description || <span className="text-muted">Sem descrição</span>}</td>
                      <td>
                        {permission.usageCount > 0 ? (
                          <div>
                            <Badge bg="info" className="me-2">
                              <FaUsers className="me-1" />
                              {permission.usageCount} perfil(s)
                            </Badge>
                            <div className="mt-1">
                              {permission.usedIn.slice(0, 2).map((usage, index) => (
                                <div key={index} className="small text-muted">
                                  • {usage.roleName} ({usage.systemName})
                                </div>
                              ))}
                              {permission.usedIn.length > 2 && (
                                <div className="small text-muted">
                                  • +{permission.usedIn.length - 2} mais...
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge bg="secondary">Não utilizada</Badge>
                        )}
                      </td>
                      <td>{formatarData(permission.created_at)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openModal('edit', permission)}
                            title="Editar permissão"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeletePermission(permission.id)}
                            title="Excluir permissão"
                            disabled={permission.usageCount > 0}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            
            {filteredPermissions.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-muted">Nenhuma permissão encontrada.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Modal para criar/editar permissão */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShieldAlt className="me-2" />
            {modalType === 'create' ? 'Nova Permissão' : 'Editar Permissão'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Permissão *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({
                  ...formData,
                  name: e.target.value.toLowerCase()
                })}
                placeholder="Ex: manage_users, create_products"
                required
              />
              <Form.Text className="text-muted">
                Use nomes descritivos no formato snake_case (ex: manage_users, create_products). Apenas letras minúsculas, números e underscore.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({
                  ...formData,
                  description: e.target.value
                })}
                placeholder="Descreva o que esta permissão permite fazer..."
              />
              <Form.Text className="text-muted">
                Descrição clara do que esta permissão permite ao usuário fazer no sistema.
              </Form.Text>
            </Form.Group>

            {modalType === 'create' && (
              <Card className="mt-3">
                <Card.Header>
                  <h6 className="mb-0">
                    <FaShieldAlt className="me-2" />
                    Permissões Sugeridas
                  </h6>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Clique em uma permissão sugerida para preenchê-la no formulário:
                  </p>
                  <div className="row">
                    {permissoesSugeridas.map((permissao, index) => (
                      <div key={index} className="col-md-6 mb-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="w-100 text-start"
                          onClick={() => adicionarPermissaoSugerida(permissao)}
                        >
                          <div>
                            <code className="text-primary">{permissao.name}</code>
                            <small className="d-block text-muted">
                              {permissao.description}
                            </small>
                          </div>
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={modalType === 'create' ? handleCreatePermission : handleUpdatePermission}
            disabled={loading || !formData.name.trim()}
          >
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            {modalType === 'create' ? 'Criar Permissão' : 'Atualizar Permissão'}
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
};

export default PermissionManagement;